// src/follower/myTrades.js
// Phase 5 — followerMarketEvent + tradeEvent listeners. FollowerMarketChart + FollowerBotPanel.

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import socketIOClient from 'socket.io-client';
import config from '../config';
import BrokerLinkModal from './components/BrokerLinkModal';
import AccountStatusBar from './components/AccountStatusBar';
import GuardrailsPanel from './components/GuardrailsPanel';
import OpenTradesPanel from './components/OpenTradesPanel';
import TradeHistoryPanel    from './components/TradeHistoryPanel';
import FollowerMarketChart  from './components/FollowerMarketChart';
import FollowerBotPanel     from './components/FollowerBotPanel';
import { mockExplanations, mockPrefs, mockStatus } from './mock/mockData';

const C = {
    bg:     "#08101e",
    bg1:    "#0c1525",
    border: "#1e2d42",
    text:   "#e8edf5",
    body:   "#b8c8dc",
    meta:   "#6a7d95",
    green:  "#00e896",
    cyan:   "#00c8ff",
    amber:  "#ffb340",
    red:    "#ff4466",
};
const FONT = "'DM Mono', monospace";

function isMarketOpen() {
    const now = new Date();
    const et  = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
    const day  = et.getDay();
    const mins = et.getHours() * 60 + et.getMinutes();
    return day >= 1 && day <= 5 && mins >= 570 && mins < 960;
}

// EC2 auth header — matches admin.js pattern: x-api-key + raw authorizationToken (no Bearer prefix)
function ec2Headers() {
    const token = sessionStorage.getItem('token');
    return {
        'x-api-key':        config.API_KEY,
        'authorizationToken': token,
        'Content-Type':     'application/json',
    };
}

// ── LimitBanner (4D) ──────────────────────────────────────────────────────────
function LimitBanner({ banner, onDismiss, onResume }) {
    if (!banner) return null;
    const isLoss   = banner.type === 'MAX_LOSS_BREACHED';
    const color    = isLoss ? C.red : C.green;
    const label    = isLoss ? 'Max Loss Breached' : 'Max Profit Reached';
    const pnlAbs   = Math.abs(banner.daily_pnl ?? 0).toFixed(2);
    const pnlStr   = (banner.daily_pnl ?? 0) >= 0 ? `+$${pnlAbs}` : `-$${pnlAbs}`;

    return (
        <div style={{
        background: C.amber + "0d", border: `1px solid ${C.amber}40`,
            borderRadius: 8, padding: "12px 20px",
            display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
    }}>
<div style={{ width: 8, height: 8, borderRadius: "50%", background: color, boxShadow: `0 0 8px ${color}`, flexShrink: 0 }} />
<div style={{ flex: 1, minWidth: 200 }}>
<div style={{ fontFamily: FONT, fontSize: 11, fontWeight: 500, color: color, letterSpacing: ".5px", marginBottom: 2 }}>
    {label}
</div>
    <div style={{ fontFamily: FONT, fontSize: 9, color: C.body, letterSpacing: ".3px" }}>
    Daily P&amp;L: <span style={{ color }}>{pnlStr}</span>
    {banner.message ? ` — ${banner.message}` : ""}
    {" "}Trading has been paused automatically.
    </div>
    </div>
    <button
    onClick={onResume}
    style={{
        fontFamily: FONT, fontSize: 9, fontWeight: 500, letterSpacing: "1px",
            padding: "7px 16px", borderRadius: 4,
            border: `1px solid ${C.green}40`, background: C.green + "12",
            color: C.green, cursor: "pointer",
    }}
>
    RESUME
    </button>
    <button
    onClick={onDismiss}
    style={{
        background: "transparent", border: "none",
            color: C.meta, cursor: "pointer", fontFamily: FONT, fontSize: 12, padding: "0 4px",
    }}
>
✕
</button>
    </div>
);
}

function ModeSwitchModal({ currentMode, prefs, onConfirm, onCancel }) {
    const toMode   = currentMode === "paper" ? "live" : "paper";
    const toLive   = toMode === "live";
    const accentTo = toLive ? C.green : C.amber;
    const labelTo  = toLive ? "LIVE" : "PAPER";

    return (
        <div style={{
        position: "fixed", inset: 0, zIndex: 3000,
            background: "rgba(4,8,18,0.90)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }}>
<div style={{ width: "100%", maxWidth: 420, background: C.bg1, border: `1px solid ${accentTo}40`, borderRadius: 10, overflow: "hidden" }}>
<div style={{ padding: "16px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10 }}>
<div style={{ width: 8, height: 8, borderRadius: "50%", background: accentTo, boxShadow: `0 0 8px ${accentTo}`, flexShrink: 0 }} />
<span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 500, color: C.text, letterSpacing: ".5px" }}>
    Switch to {labelTo} Mode
    </span>
    </div>
    <div style={{ padding: "20px 24px" }}>
    {toLive ? (
    <div style={{ background: C.red + "0e", border: `1px solid ${C.red}30`, borderRadius: 6, padding: "10px 14px", marginBottom: 20, display: "flex", gap: 10, alignItems: "flex-start" }}>
    <div style={{ width: 4, height: 4, borderRadius: "50%", background: C.red, marginTop: 5, flexShrink: 0 }} />
    <span style={{ fontFamily: FONT, fontSize: 10, color: C.red, lineHeight: 1.6, letterSpacing: ".3px" }}>
    <strong>Real money will be traded.</strong> BerBul will place live orders on your Alpaca account using your guardrails below.
    </span>
    </div>
    ) : (
    <div style={{ background: C.amber + "0d", border: `1px solid ${C.amber}25`, borderRadius: 6, padding: "10px 14px", marginBottom: 20, display: "flex", gap: 10, alignItems: "flex-start" }}>
    <div style={{ width: 4, height: 4, borderRadius: "50%", background: C.amber, marginTop: 5, flexShrink: 0 }} />
    <span style={{ fontFamily: FONT, fontSize: 10, color: C.amber, lineHeight: 1.6, letterSpacing: ".3px" }}>
        Switching to paper mode. Any open live positions are <strong>not</strong> automatically closed — manage them in your Alpaca dashboard.
    </span>
    </div>
    )}
<div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "12px 16px", marginBottom: 24 }}>
<div style={{ fontFamily: FONT, fontSize: 8, color: C.meta, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 10 }}>Active Guardrails</div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
    {[
        { label: "Max Contracts",    value: prefs.max_contracts },
        { label: "Max Daily Loss",   value: `$${prefs.max_daily_loss}` },
        { label: "Max Daily Profit", value: `$${prefs.max_daily_profit}` },
    ].map(({ label, value }) => (
    <div key={label}>
        <div style={{ fontFamily: FONT, fontSize: 8, color: C.meta, letterSpacing: ".5px", marginBottom: 3 }}>{label}</div>
    <div style={{ fontFamily: FONT, fontSize: 12, color: C.text }}>{value}</div>
    </div>
    ))}
</div>
    </div>
    <p style={{ fontFamily: FONT, fontSize: 10, color: C.body, lineHeight: 1.7, margin: "0 0 20px" }}>
    Confirm you want to switch from{" "}
<strong style={{ color: currentMode === "paper" ? C.amber : C.green }}>{currentMode.toUpperCase()}</strong>
    {" "}to{" "}
<strong style={{ color: accentTo }}>{labelTo}</strong> mode.
    </p>
    <div style={{ display: "flex", gap: 10 }}>
<button onClick={onCancel} style={{ flex: 1, fontFamily: FONT, fontSize: 9, letterSpacing: "1px", padding: "9px", borderRadius: 4, border: `1px solid ${C.border}`, background: "transparent", color: C.meta, cursor: "pointer" }}>
    CANCEL
    </button>
    <button onClick={onConfirm} style={{ flex: 1, fontFamily: FONT, fontSize: 9, fontWeight: 500, letterSpacing: "1px", padding: "9px", borderRadius: 4, border: `1px solid ${accentTo}50`, background: accentTo + "18", color: accentTo, cursor: "pointer" }}>
    SWITCH TO {labelTo}
</button>
    </div>
    </div>
    </div>
    </div>
);
}

export default function MyTrades() {
    const history = useHistory();

    // ── State ─────────────────────────────────────────────────────────────────
    const [loading,         setLoading]         = useState(true);
    const [brokerLinked,    setBrokerLinked]     = useState(false);
    const [showBrokerModal, setShowBrokerModal]  = useState(false);
    const [showModeModal,   setShowModeModal]    = useState(false);
    const [prefs,           setPrefs]            = useState(null);
    const [openPositions,   setOpenPositions]    = useState([]);
    const [closedPositions, setClosedPositions]  = useState([]);
    const [dailyPnL,        setDailyPnL]         = useState(0);
    const [explanations,    setExplanations]     = useState(mockExplanations);
    const [limitBanner,     setLimitBanner]      = useState(null);
    const [pendingOrders,   setPendingOrders]    = useState({}); // keyed by master_position_id
    const lastSyncAtRef = useRef(null);
    const [lastSyncAt,  setLastSyncAt]  = useState(null);
    // ── Phase 5: chart + bot panel data ───────────────────────────────────────
    const [followerBars,  setFollowerBars]  = useState([]); // merged followerMarketEvent + tradeEvent
    const [currentPrice,  setCurrentPrice]  = useState(0);  // from tradeEvent.close

    const socketRef    = useRef(null);
    const retryTimerRef = useRef(null);

    const marketOpen = isMarketOpen();
    const role       = sessionStorage.getItem('role') || 'admin';
    const isDemo     = role === 'demo';

    // ── Helpers ───────────────────────────────────────────────────────────────
    const lambdaHeaders = useCallback(() => {
        const token = sessionStorage.getItem('token');
        return { 'x-api-key': config.API_KEY, 'Authorization': `Bearer ${token}` };
    }, []);

    const fetchPrefsAndStatus = useCallback(async () => {
        const headers = lambdaHeaders();
        try {
            const prefsRes = await axios.get(config.API_BASE_URL + '/follower/prefs', { headers });
            const p = prefsRes.data;
            setPrefs(p);
            setBrokerLinked(p.paper_keys_linked || false);
        } catch (err) {
            if (err?.response?.status === 401) {
                history.push('/Login');
                return;
            }
            // 404 = no broker linked yet
            setBrokerLinked(false);
            return;
        }
        try {
            const statusRes = await axios.get(config.API_SOCKET_URL + '/follower/status', { headers: ec2Headers() });
            const s = statusRes.data;
            const allPositions = [...(s.open_positions || []), ...(s.closed_positions || [])];

            // Build explanations dict from inline fields on each position
            const explanationsFromStatus = {};
            allPositions.forEach(pos => {
                if (pos.entry_explanation || pos.exit_explanation || pos.close_reason) {
                    explanationsFromStatus[pos.master_position_id] = {
                        entry_explanation: pos.entry_explanation || '',
                        exit_explanation:  pos.exit_explanation  || '',
                        close_reason:      pos.close_reason      || '',
                        reactive_signal:   pos.reactive_signal   || '',
                    };
                }
            });
            setExplanations(prev => ({ ...prev, ...explanationsFromStatus }));

            setOpenPositions(s.open_positions || []);
            setClosedPositions(s.closed_positions || []);
            setDailyPnL(s.daily_pnl ?? 0);
        } catch (err) {
            console.log('Status not available:', err.message);
        }
    }, [lambdaHeaders, history]);

    // ── Page load ─────────────────────────────────────────────────────────────
    useEffect(() => {
        if (isDemo) {
            setPrefs(mockPrefs);
            setOpenPositions(mockStatus.open_positions || mockStatus.open || []);
            setDailyPnL(mockStatus.daily_pnl);
            setBrokerLinked(true);
            setLoading(false);
            return;
        }
        fetchPrefsAndStatus().finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Socket lifecycle (4A) ─────────────────────────────────────────────────
    const connectSocket = useCallback(() => {
        if (socketRef.current) return; // already connected
        if (!isMarketOpen()) return;   // market hours gate

        const token = sessionStorage.getItem('token');
        const socket = socketIOClient(config.API_SOCKET_URL, {
            transports:   ['websocket', 'polling'],
            query:        { token },
            reconnection: false, // we manage retry manually
        });

        // 4B — servantPositionEvent
        socket.on('servantPositionEvent', (event) => {
            try {
                lastSyncAtRef.current = Date.now();
                setLastSyncAt(lastSyncAtRef.current);
                const payload = JSON.parse(event.data);
                const { master_position_id, status } = payload;

                if (status === 'open') {
                    setOpenPositions(prev => {
                        const idx = prev.findIndex(p => p.master_position_id === master_position_id);
                        if (idx >= 0) {
                            const next = [...prev];
                            next[idx] = { ...next[idx], ...payload };
                            return next;
                        }
                        return [...prev, payload];
                    });
                } else if (status === 'closed') {
                    setOpenPositions(prev => prev.filter(p => p.master_position_id !== master_position_id));
                    setClosedPositions(prev => {
                        const deduped = prev.filter(p => p.master_position_id !== master_position_id);
                        const next    = [...deduped, payload]; // newest-last (oldest first) — FollowerBotPanel reads [length-1]
                        const total   = next.reduce((sum, p) => sum + (p.realized_pnl ?? 0), 0);
                        setDailyPnL(parseFloat(total.toFixed(2)));
                        return next;
                    });
                } else if (status === 'failed') {
                    setOpenPositions(prev => prev.filter(p => p.master_position_id !== master_position_id));
                }
            } catch (err) {
                console.error('servantPositionEvent parse error:', err);
            }
        });

        // 4B — servantExplanationEvent
        socket.on('servantExplanationEvent', (event) => {
            try {
                const payload = JSON.parse(event.data);
                setExplanations(prev => ({
                    ...prev,
                    [payload.master_position_id]: {
                        ...prev[payload.master_position_id],
                        [`${payload.type}_explanation`]: payload.explanation,
                        close_reason:    payload.close_reason    ?? '',
                        reactive_signal: payload.reactive_signal ?? '',
                    },
                }));
            } catch (err) {
                console.error('servantExplanationEvent parse error:', err);
            }
        });

        // 4B — servantLimitEvent
        socket.on('servantLimitEvent', (event) => {
            try {
                const payload = JSON.parse(event.data);
                setLimitBanner({ type: payload.type, daily_pnl: payload.daily_pnl, limit: payload.limit, message: payload.message });
                if (payload.daily_pnl !== undefined) setDailyPnL(payload.daily_pnl);
                // Reflect paused state in prefs
                setPrefs(prev => prev ? { ...prev, is_paused: true, pause_reason: payload.type } : prev);
            } catch (err) {
                console.error('servantLimitEvent parse error:', err);
            }
        });

        // 4B — servantOrderEvent
        socket.on('servantOrderEvent', (event) => {
            try {
                const payload = JSON.parse(event.data);
                setPendingOrders(prev => ({
                    ...prev,
                    [payload.master_position_id]: payload,
                }));
            } catch (err) {
                console.error('servantOrderEvent parse error:', err);
            }
        });

        // 5A — followerMarketEvent
        // History array on connect (auto-emitted by server), single bar per minute thereafter.
        // Each bar carries OHLC + flat eval_* fields (50 fields total, all primitives).
        socket.on('followerMarketEvent', (event) => {
            try {
                const payload = JSON.parse(event.data);
                const bars    = Array.isArray(payload) ? payload : [payload];

                setCurrentPrice(prev => {
                    const last = bars[bars.length - 1];
                    return last?.close ? parseFloat(last.close) : prev;
                });

                setFollowerBars(prev => {
                    if (Array.isArray(payload)) {
                        // Full history replace on connect — seed chart immediately
                        return bars.slice(-390);
                    }
                    // Single bar upsert — same timestamp-keyed merge as tradeEvent
                    const bar = bars[0];
                    if (!bar?.t) return prev;
                    if (prev.length === 0 || bar.t > prev[prev.length - 1].t) {
                        return [...prev, bar].slice(-390);
                    }
                    if (bar.t === prev[prev.length - 1].t) {
                        // Preserve existing eval_* fields, update OHLC from bar
                        const next = [...prev];
                        next[next.length - 1] = { ...next[next.length - 1], ...bar };
                        return next;
                    }
                    return prev;
                });
            } catch (err) {
                console.error('followerMarketEvent parse error:', err);
            }
        });

        // 5B — tradeEvent (follower-scoped)
        // Sub-minute price tick: { t, symbol, open, high, low, close, volume,
        //                          eval_decision, eval_position_id }
        // Merges into followerBars by timestamp.
        // Spread order: prev bar first so eval_* written by followerMarketEvent are preserved.
        socket.on('tradeEvent', (event) => {
            try {
                const bar = JSON.parse(event.data);
                if (!bar?.t) return;

                // Normalise to minute boundary — matches server_v5 bar cadence
                const dt = new Date(bar.t);
                dt.setSeconds(0); dt.setMilliseconds(0);
                bar.t = dt.toISOString().replace('.000Z', '.000000000Z');

                if (bar.close) setCurrentPrice(parseFloat(bar.close));

                setFollowerBars(prev => {
                    if (prev.length === 0 || bar.t > prev[prev.length - 1].t) {
                        return [...prev, bar].slice(-390);
                    }
                    if (bar.t === prev[prev.length - 1].t) {
                        const next = [...prev];
                        // prev bar spreads first — protects eval_* from followerMarketEvent
                        next[next.length - 1] = { ...next[next.length - 1], ...bar };
                        return next;
                    }
                    return prev;
                });
            } catch (err) {
                console.error('tradeEvent parse error:', err);
            }
        });

        // 5C — request full session history on connect
        // Server responds with followerMarketEvent carrying the full array.
        // Mirrors getPredictionHistory pattern on the master socket.
        socket.emit('getFollowerMarketHistory', {});

        socket.on('disconnect', () => {
            socketRef.current = null;
            // Retry every 30s if still market hours
            retryTimerRef.current = setTimeout(async () => {
                if (isMarketOpen()) {
                    await fetchPrefsAndStatus();
                    connectSocket();
                }
            }, 30000);
        });

        socketRef.current = socket;
    }, [fetchPrefsAndStatus]);

    useEffect(() => {
        if (isDemo || !brokerLinked) return;
        connectSocket();
        return () => {
            if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; }
            if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); }
        };
    }, [brokerLinked, isDemo, connectSocket]);

    // ── Phase 5: derived metrics for FollowerBotPanel ─────────────────────────
    const metrics = useMemo(() => {
        const total = closedPositions.length;
        const wins  = closedPositions.filter(p => (p.realized_pnl ?? 0) >= 0).length;
        return {
            closedTrades: total,
            winRate:      total === 0 ? 0 : wins / total,
        };
    }, [closedPositions]);

    // ── Resume from limitBanner ───────────────────────────────────────────────
    const handleBannerResume = useCallback(async () => {
        try {
            await axios.post(config.API_SOCKET_URL + '/follower/resume', {}, { headers: ec2Headers() });
            setPrefs(prev => prev ? { ...prev, is_paused: false, pause_reason: '' } : prev);
            setLimitBanner(null);
        } catch (err) {
            console.error('Banner resume error:', err);
        }
    }, []);

    const handleConfirmModeSwitch = () => {
        const newMode = prefs.trading_mode === "paper" ? "live" : "paper";
        // Phase 2: call Lambda to update DynamoDB, then POST /follower/reload-prefs to EC2
        setPrefs(prev => ({ ...prev, trading_mode: newMode }));
        setShowModeModal(false);
    };

    if (loading) {
        return (
            <div style={{ background: '#08101e', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <span style={{ fontFamily: FONT, fontSize: 10, color: '#6a7d95', letterSpacing: '1px' }}>LOADING...</span>
        </div>
    );
    }

    if (!brokerLinked) {
        return (
            <div style={{ background: C.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>

        {showBrokerModal && (
        <BrokerLinkModal
            onCancel={() => setShowBrokerModal(false)}
            onSuccess={async () => {
            try {
                const token = sessionStorage.getItem('token');
                const res = await axios.get(config.API_BASE_URL + '/follower/prefs', {
                    headers: { 'x-api-key': config.API_KEY, 'Authorization': `Bearer ${token}` }
                });
                setPrefs(res.data);
            } catch (err) {
                console.error('Prefs reload error:', err);
            }
            setShowBrokerModal(false);
            setBrokerLinked(true);
        }}
        />
        )}

    <div style={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>

        {/* Icon */}
    <div style={{
            width: 56, height: 56, borderRadius: '50%',
                background: C.cyan + '12', border: `1px solid ${C.cyan}30`,
                display: 'grid', placeItems: 'center',
                margin: '0 auto 24px',
        }}>
    <div style={{ width: 20, height: 20, borderRadius: '50%', background: C.cyan, opacity: 0.7 }} />
    </div>

        {/* Headline */}
        <div style={{ fontFamily: FONT, fontSize: 18, fontWeight: 500, color: C.text, letterSpacing: '.5px', marginBottom: 12 }}>
        Welcome to BerBul
        </div>
        <div style={{ fontFamily: FONT, fontSize: 11, color: C.body, lineHeight: 1.7, marginBottom: 8 }}>
        You're all set. BerBul will trade SPY 0DTE options autonomously on your behalf — you just need to connect your Alpaca account to get started.
        </div>
        <div style={{ fontFamily: FONT, fontSize: 10, color: C.meta, lineHeight: 1.6, marginBottom: 32 }}>
        Don't have an Alpaca account yet? No rush — create one at <span style={{ color: C.cyan }}>alpaca.markets</span> and come back when you're ready.
        </div>

        {/* Steps */}
        <div style={{
            background: C.bg1, border: `1px solid ${C.border}`,
                borderRadius: 10, padding: '20px 24px', marginBottom: 28, textAlign: 'left',
        }}>
    <div style={{ fontFamily: FONT, fontSize: 8, color: C.meta, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 16 }}>
        To get started
        </div>
        {[
        { n: '1', text: 'Create a dedicated Alpaca paper trading account' },
        { n: '2', text: 'Copy your paper API key and secret' },
        { n: '3', text: 'Connect your broker below and set your guardrails' },
    ].map(({ n, text }) => (
        <div key={n} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
    <div style={{
            width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                background: C.cyan + '15', border: `1px solid ${C.cyan}30`,
                display: 'grid', placeItems: 'center',
                fontFamily: FONT, fontSize: 9, color: C.cyan,
        }}>
        {n}
    </div>
        <span style={{ fontFamily: FONT, fontSize: 10, color: C.body, lineHeight: 1.6, paddingTop: 2 }}>{text}</span>
        </div>
    ))}
    </div>

        {/* CTA */}
        <button
        onClick={() => setShowBrokerModal(true)}
        style={{
            width: '100%', padding: '12px',
                fontFamily: FONT, fontSize: 10, fontWeight: 500,
                letterSpacing: '1.2px', textTransform: 'uppercase',
                borderRadius: 6, border: `1px solid ${C.cyan}50`,
                background: C.cyan + '15', color: C.cyan,
                cursor: 'pointer', transition: 'background .15s',
        }}
    >
        Connect Alpaca Account
        </button>

        </div>
        </div>
    );
    }

    return (
        <div style={{ background: '#08101e', minHeight: '100vh', padding: '32px' }}>

    {showBrokerModal && (
    <BrokerLinkModal
        onCancel={() => setShowBrokerModal(false)}
        onSuccess={async () => {
        try {
            const token = sessionStorage.getItem('token');
            const res = await axios.get(config.API_BASE_URL + '/follower/prefs', {
                headers: { 'x-api-key': config.API_KEY, 'Authorization': `Bearer ${token}` }
            });
            setPrefs(res.data);
        } catch (err) {
            console.error('Prefs reload error:', err);
        }
        setShowBrokerModal(false);
    }} />
    )}
    {showModeModal && (
    <ModeSwitchModal
        currentMode={prefs.trading_mode}
        prefs={prefs}
        onConfirm={handleConfirmModeSwitch}
        onCancel={() => setShowModeModal(false)}
    />
    )}

    <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>

    {/* Limit Banner (4D) — above status bar when a limit event arrives */}
    <LimitBanner
    banner={limitBanner}
    onDismiss={() => setLimitBanner(null)}
    onResume={handleBannerResume}
        />

        {/* Section A — Account Status Bar */}
        <AccountStatusBar
    prefs={prefs}
    dailyPnL={dailyPnL}
    openCount={openPositions.length}
    lastSyncAt={lastSyncAt}
    onEditBroker={isDemo ? null : () => setShowBrokerModal(true)}
    onToggleMode={isDemo ? null : () => setShowModeModal(true)}
        />

        {/* Section B — Guardrails Panel */}
        <GuardrailsPanel
    prefs={prefs}
    onPrefsChange={setPrefs}
    onEmergencyClose={() => {}}
    marketOpen={marketOpen}
    isDemo={isDemo}
        />

        {/* Section B2 — Agent Intelligence (FollowerBotPanel) */}
        <FollowerBotPanel
    followerBars={followerBars}
    currentPrice={currentPrice}
    openPositions={openPositions}
    closedPositions={closedPositions}
    metrics={metrics}
    onPauseBot={() => {}}
    />

    {/* Section B3 — SPY Market Chart with AI markers */}
    <FollowerMarketChart
    data={followerBars}
    selectedPosition={openPositions[0] ?? null}
        />

        {/* Section C — Open Trades */}
        <OpenTradesPanel
    positions={openPositions}
    explanations={explanations}
    pendingOrders={pendingOrders}
        />

        {/* Section D — Trade History */}
        <TradeHistoryPanel
    positions={closedPositions}
    explanations={explanations}
    dailyPnL={dailyPnL}
        />

        </div>
        </div>
    );
}